import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom'; // ← thêm
import { useBlogStore } from '@/store/blogStore';
import SEOHead from '@/components/common/SEOHead';
import { 
  ArticleCard, 
  SearchBar, 
  TopicFilter, 
  Breadcrumb 
} from '@/components/blog';
import { LoadingSpinner, ErrorState } from '@/components/common';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon,
  Squares2X2Icon,
  ListBulletIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { SortOption } from '@/types/blog';

// Hook lấy query param
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const BlogHomepage = () => {
  const {
    posts,
    featuredPosts,
    searchResults,
    topics,
    loading,
    searchLoading,
    topicsLoading,
    error,
    pagination,
    searchState,
    fetchPosts,
    fetchFeaturedPosts,
    fetchTopics,
    searchPosts,
    clearSearchResults,
    updateSearchState,
    setSortOrder,
  // loadPage,
    loadNextPage,
    loadPrevPage,
    likePost,
    sharePost,
    clearError
  } = useBlogStore();

  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [showFeaturedArticles, setShowFeaturedArticles] = useState(true);

  const query = useQuery(); // ← thêm
  const navigate = useNavigate(); // ← thêm
  const searchFromURL = query.get('search') || ''; // ← thêm

  // Initialize data on component mount
  useEffect(() => {
    fetchFeaturedPosts();
    fetchTopics();
    fetchPosts({ sort: 'newest' });
  }, [fetchFeaturedPosts, fetchTopics, fetchPosts]);

  // Tự động search nếu có query param
  useEffect(() => {
    if (searchFromURL) {
      updateSearchState({ searchQuery: searchFromURL, page: 1 }); // ← cập nhật searchState
      handleSearch(searchFromURL); // ← gọi API search
    }
  }, [searchFromURL]);

  // Handle search
  const handleSearch = async (query: string) => {
    if (query.trim()) {
      await searchPosts(query, {
        topic_id: searchState.selectedTopicId,
        page: 1,
        limit: searchState.limit
      });
    } else {
      clearSearchResults();
      await fetchPosts({
        topic_id: searchState.selectedTopicId,
        sort: searchState.sortBy,
        page: 1,
        limit: searchState.limit
      });
    }
  };

  // Handle topic filter change
  const handleTopicChange = async (topicId: number | undefined) => {
    updateSearchState({ selectedTopicId: topicId, page: 1 });
    
    if (searchState.searchQuery) {
      await searchPosts(searchState.searchQuery, {
        topic_id: topicId,
        page: 1,
        limit: searchState.limit
      });
    } else {
      await fetchPosts({
        topic_id: topicId,
        sort: searchState.sortBy,
        page: 1,
        limit: searchState.limit
      });
    }
  };

  // Handle sort change
  const handleSortChange = async (sort: SortOption) => {
    await setSortOrder(sort);
  };

  // Handle like post
  const handleLikePost = async (uuid: string) => {
    await likePost(uuid);
  };

  // Handle share post
  const handleSharePost = async (uuid: string, platform: 'facebook' | 'linkedin' | 'zalo') => {
    await sharePost(uuid, platform);
  };

  // Determine which posts to display
  const displayPosts = searchState.searchQuery ? searchResults : posts;
  // const hasSearchResults = searchState.searchQuery && searchResults.length > 0;
  const isSearchMode = Boolean(searchState.searchQuery);

  // Breadcrumb items
  const breadcrumbItems = [
    { label: 'Blog', href: '/blog' }
  ];

  if (error) {
    return (
      <div className="eh-container py-8">
        <ErrorState 
          message={error} 
          onRetry={() => {
            clearError();
            fetchPosts();
            fetchFeaturedPosts();
            fetchTopics();
          }} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-m2-background">
      <SEOHead
        title="M2MBA Blog - Chia sẻ kiến thức và kinh nghiệm về đào tạo và phát triển doanh nghiệp"
        description="Nơi chia sẻ kiến thức và kinh nghiệm về đào tạo và phát triển doanh nghiệp, giúp bạn và đội ngũ của mình tiến xa hơn."
        image="https://storage.googleapis.com/m2mba/f4872d86-57b4-4bb8-9492-f14a60f22280.jpg"
        noindex={false}
        url="/blog"
        type="website"
      />
      {/* Header Section */}
      <div className="border-b border-eh-border bg-eh-sage/50">
        <div className="eh-container py-8 md:py-10">
          <Breadcrumb items={breadcrumbItems} className="mb-4" />
          
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="eh-eyebrow">Góc nhìn thực chiến</p>
              <h1 className="eh-display mb-3">
                Nơi chia sẻ kiến thức và kinh nghiệm
              </h1>
              <p className="text-eh-muted leading-relaxed">
                Chào mừng bạn đến với blog của chúng tôi! Đây là nơi bạn có thể khám phá những kiến thức và kinh nghiệm hữu ích về đào tạo và phát triển doanh nghiệp, giúp bạn và đội ngũ của mình tiến xa hơn
              </p>
            </div>

            {/* Search and Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 min-w-0 lg:min-w-[500px]">
              <div className="flex-1">
                <SearchBar
                  onSearch={handleSearch}
                  loading={searchLoading}
                  defaultValue={searchState.searchQuery}
                  placeholder="Tìm kiếm bài viết..."
                />
              </div>
              
              {/* Filter Toggle Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-4 py-3 border rounded-eh transition-colors flex items-center gap-2 whitespace-nowrap ${
                  showFilters 
                    ? 'bg-m2-primary/10 border-m2-primary/40 text-m2-primary'
                    : 'border-m2-outline text-m2-on-surface hover:bg-m2-surface-low'
                }`}
              >
                {showFilters ? (
                  <XMarkIcon className="h-4 w-4" />
                ) : (
                  <FunnelIcon className="h-4 w-4" />
                )}
                Lọc
              </button>
            </div>
          </div>

          {/* Active Filter Indicators */}
          {(searchState.selectedTopicId || searchState.sortBy !== 'newest') && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-sm text-m2-on-surface-variant">Đang lọc:</span>
              
              {/* Active Topic Filter */}
              {searchState.selectedTopicId && (
                <div className="flex items-center gap-1 px-3 py-1 bg-m2-primary/15 text-m2-primary rounded-full text-sm">
                  <span>Chủ đề: {topics.find(t => t.id === searchState.selectedTopicId)?.title || 'Không xác định'}</span>
                  <button
                    onClick={() => handleTopicChange(undefined)}
                    className="ml-1 text-m2-primary hover:text-m2-primary"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
              
              {/* Active Sort Filter */}
              {searchState.sortBy !== 'newest' && (
                <div className="flex items-center gap-1 px-3 py-1 bg-m2-primary/15 text-m2-primary rounded-full text-sm">
                  <span>
                    Sắp xếp: {
                      searchState.sortBy === 'oldest' ? 'Cũ nhất' :
                      searchState.sortBy === 'most_viewed' ? 'Xem nhiều nhất' : 'Mới nhất'
                    }
                  </span>
                  <button
                    onClick={() => handleSortChange('newest')}
                    className="ml-1 text-m2-primary hover:text-m2-primary"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </div>
              )}

              {/* Clear All Filters */}
              <button
                onClick={() => {
                  handleTopicChange(undefined);
                  handleSortChange('newest');
                }}
                className="text-xs text-m2-on-surface-variant hover:text-m2-on-surface underline"
              >
                Xóa tất cả
              </button>
            </div>
          )}

          {/* Collapsible Filter Panel */}
          {showFilters && (
            <div className="mt-4 p-4 bg-eh-cream rounded-eh border border-eh-border shadow-eh-soft">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Topic Filter */}
                <div>
                  <label className="block text-sm font-medium text-m2-on-surface mb-2">
                    Chủ đề
                  </label>
                  <TopicFilter
                    topics={topics}
                    selectedTopicId={searchState.selectedTopicId}
                    onTopicChange={handleTopicChange}
                    loading={topicsLoading}
                  />
                </div>
                
                {/* Sort Filter */}
                <div>
                  <label className="block text-sm font-medium text-m2-on-surface mb-2">
                    Sắp xếp theo
                  </label>
                  <select
                    value={searchState.sortBy}
                    onChange={(e) => handleSortChange(e.target.value as SortOption)}
                    className="w-full px-3 py-3 border border-eh-border rounded-eh focus:outline-none focus:ring-2 focus:ring-eh-forest-ink/30 focus:border-eh-forest-ink bg-m2-surface-lowest"
                  >
                    <option value="newest">Mới nhất</option>
                    <option value="oldest">Cũ nhất</option>
                    <option value="most_viewed">Xem nhiều nhất</option>
                  </select>
                </div>

                {/* View Mode */}
                <div>
                  <label className="block text-sm font-medium text-m2-on-surface mb-2">
                    Hiển thị
                  </label>
                  <div className="flex border border-eh-border rounded-eh overflow-hidden">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`flex-1 p-3 text-center ${
                        viewMode === 'grid'
                          ? 'bg-m2-primary text-m2-on-primary'
                          : 'bg-m2-surface-container text-m2-on-surface hover:bg-m2-surface-low'
                      } transition-colors`}
                      title="Xem dạng lưới"
                    >
                      <Squares2X2Icon className="h-4 w-4 mx-auto" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex-1 p-3 text-center ${
                        viewMode === 'list'
                          ? 'bg-m2-primary text-m2-on-primary'
                          : 'bg-m2-surface-container text-m2-on-surface hover:bg-m2-surface-low'
                      } transition-colors`}
                      title="Xem dạng danh sách"
                    >
                      <ListBulletIcon className="h-4 w-4 mx-auto" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="eh-container py-10">
        {/* Featured Articles Section - Only show when not searching */}
        {!isSearchMode && featuredPosts.length > 0 && (
          <section className="mb-1">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold text-m2-on-surface">Bài viết nổi bật</h2>
                <button
                  onClick={() => setShowFeaturedArticles(!showFeaturedArticles)}
                  className="flex items-center justify-center w-8 h-8 rounded-full border border-m2-outline text-m2-on-surface-variant hover:text-m2-on-surface hover:border-m2-outline transition-colors"
                  title={showFeaturedArticles ? 'Thu gọn bài viết nổi bật' : 'Mở rộng bài viết nổi bật'}
                >
                  {showFeaturedArticles ? (
                    <ChevronUpIcon className="h-4 w-4" />
                  ) : (
                    <ChevronDownIcon className="h-4 w-4" />
                  )}
                </button>
              </div>
              <div className="h-px bg-gradient-to-r from-m2-primary/30 to-transparent flex-1 ml-4"></div>
            </div>
            
            <div className={`transition-all duration-300 ease-in-out ${
              showFeaturedArticles 
                ? 'max-h-none opacity-100' 
                : 'max-h-0 opacity-0 overflow-hidden'
            }`}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {featuredPosts.slice(0, 3).map((post) => (
                  <ArticleCard
                    key={post.uuid}
                    post={post}
                    featured={true}
                    onLike={handleLikePost}
                    onShare={handleSharePost}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* All Articles Section */}
        <section>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-m2-on-surface">
              {isSearchMode ? `Kết quả tìm kiếm` : 'Tất cả bài viết'}
              {pagination && (
                <span className="text-lg font-normal text-m2-on-surface-variant ml-2">
                  ({pagination.totalItems} bài viết)
                </span>
              )}
            </h2>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Empty State */}
          {!loading && displayPosts.length === 0 && (
            <div className="text-center py-12">
              <div className="text-m2-on-surface-variant mb-4">
                {isSearchMode ? 'Không tìm thấy bài viết nào' : 'Chưa có bài viết nào'}
              </div>
              {isSearchMode && (
                <button
                  onClick={() => {
                    clearSearchResults();
                    updateSearchState({ searchQuery: '' });
                  }}
                  className="text-m2-primary hover:text-m2-primary font-medium"
                >
                  Xem tất cả bài viết
                </button>
              )}
            </div>
          )}

          {/* Articles Grid */}
          {!loading && displayPosts.length > 0 && (
            <>
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
            {displayPosts.map((post) => (
              <div key={post.uuid}>
              <ArticleCard
                post={post}
                onLike={handleLikePost}
                onShare={handleSharePost}
                className={viewMode === 'list' ? 'md:flex-row' : ''}
              />
              {/* Hiển thị tag bên ngoài ArticleCard */}
              {post.tagNames && post.tagNames.length > 0 && (
                <div className="px-6 lg:px-8 pb-6 border-t mt-2">
                  <h3 className="text-sm font-semibold text-m2-on-surface mb-3 flex items-center">
                    <TagIcon className="h-4 w-4 mr-1" /> Tags:
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {post.tagNames.map((tag) => (
                      <button
                        key={tag} 
                        onClick={() => {
                          navigate(`/blog?search=${encodeURIComponent(tag)}`);
                          updateSearchState({ searchQuery: tag, page: 1 });
                          handleSearch(tag);
                        }}
                        className="inline-flex items-center px-3 py-1 rounded-eh-pill text-xs font-medium bg-eh-sage text-eh-forest-ink hover:bg-eh-keylime transition-colors"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

                ))}
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-8 pt-8 border-t">
                  <div className="text-sm text-m2-on-surface-variant">
                    Trang {pagination.currentPage} / {pagination.totalPages}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={loadPrevPage}
                      disabled={!pagination.hasPrevPage || loading}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-eh-border rounded-eh disabled:opacity-50 disabled:cursor-not-allowed hover:bg-eh-keylime transition-colors"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                      Trước
                    </button>
                    
                    <button
                      onClick={loadNextPage}
                      disabled={!pagination.hasNextPage || loading}
                      className="flex items-center gap-1 px-3 py-2 text-sm border border-eh-border rounded-eh disabled:opacity-50 disabled:cursor-not-allowed hover:bg-eh-keylime transition-colors"
                    >
                      Sau
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default BlogHomepage;
